/* ═══════════════════════════════════════════════════════════════
   VerifyEmailBanner — shown at the top of the app when the
   signed-in user hasn't verified their email yet.

   Renders nothing when emailVerified === true. Otherwise shows a
   dismissable banner with a "Enter code" CTA that expands an
   inline 6-digit input, plus a "Resend" link.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState } from 'react';
import { AuthService, type AuthUser } from '../services/authService';
import { rem } from '../lib/rem';

interface Props {
  authUser: AuthUser;
  onVerified?: () => void;
}

export function VerifyEmailBanner({ authUser, onVerified }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<
    'idle' | 'verifying' | 'resending' | 'resent' | 'verified' | 'error'
  >('idle');
  const [error, setError] = useState('');

  if (authUser.emailVerified) return null;
  if (status === 'verified') return null;

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError('Enter the 6-digit code');
      setStatus('error');
      return;
    }
    setStatus('verifying');
    setError('');
    const r = await AuthService.verifyEmail(authUser.email, code);
    if (r.error) {
      setError(r.error);
      setStatus('error');
      return;
    }
    setStatus('verified');
    onVerified?.();
  };

  const handleResend = async () => {
    setStatus('resending');
    setError('');
    const r = await AuthService.requestVerification(authUser.email);
    if (r.error) {
      setError(r.error);
      setStatus('error');
      return;
    }
    setStatus('resent');
    setTimeout(() => setStatus('idle'), 4000);
  };

  return (
    <div
      style={{
        padding: `${rem(10)} ${rem(20)}`,
        background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
        borderBottom: '1px solid color-mix(in srgb, var(--color-warning) 45%, transparent)',
        color: 'var(--color-warning)',
        fontSize: 'var(--font-size-base)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: 'var(--font-size-md)' }}>✉</span>
      <span style={{ flex: 1, minWidth: rem(200) }}>
        <strong>Verify your email.</strong> We sent a 6-digit code to <b>{authUser.email}</b>.
      </span>

      {!expanded ? (
        <>
          <button onClick={() => setExpanded(true)} style={btnPrimary}>
            Enter code
          </button>
          <button onClick={handleResend} disabled={status === 'resending'} style={btnGhost}>
            {status === 'resending' ? 'Sending…' : status === 'resent' ? 'Code sent ✓' : 'Resend'}
          </button>
        </>
      ) : (
        <>
          <input
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
              setError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleVerify();
            }}
            placeholder="000000"
            autoFocus
            style={{
              width: rem(110),
              padding: `var(--space-1) ${rem(10)}`,
              border: '1px solid var(--color-warning)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'calc(var(--font-size-base) * 15 / 13)',
              fontWeight: 700,
              textAlign: 'center',
              letterSpacing: '0.3em',
              fontFamily: "'JetBrains Mono', monospace",
              background: 'var(--color-surface)',
              color: 'var(--color-text)',
              outline: 'none',
            }}
          />
          <button
            onClick={handleVerify}
            disabled={status === 'verifying' || code.length !== 6}
            style={{
              ...btnPrimary,
              opacity: status === 'verifying' || code.length !== 6 ? 0.6 : 1,
            }}
          >
            {status === 'verifying' ? 'Verifying…' : 'Verify'}
          </button>
          <button
            onClick={() => {
              setExpanded(false);
              setCode('');
              setError('');
              setStatus('idle');
            }}
            style={btnGhost}
          >
            Cancel
          </button>
          <button onClick={handleResend} disabled={status === 'resending'} style={btnGhost}>
            {status === 'resending' ? 'Sending…' : status === 'resent' ? 'Sent ✓' : 'Resend'}
          </button>
        </>
      )}

      {error && (
        <span
          style={{
            color: 'var(--color-danger)',
            fontSize: 'calc(var(--font-size-base) * 12 / 13)',
            width: '100%',
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  padding: `${rem(5)} ${rem(14)}`,
  borderRadius: 'var(--radius-sm)',
  border: 'none',
  background: 'var(--color-warning)',
  color: 'var(--color-text-inverse)',
  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
  fontWeight: 600,
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  padding: `${rem(5)} ${rem(10)}`,
  borderRadius: 'var(--radius-sm)',
  border: '1px solid transparent',
  background: 'transparent',
  color: 'var(--color-warning)',
  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
  fontWeight: 500,
  cursor: 'pointer',
};
